import { inject, Injectable } from '@angular/core';
import { ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { transformValidationErrors } from '@fmnts/common/forms';
import { hasProperty, isDefined, isNumber } from '@fmnts/core';
import {
  ConditionalValidationService,
  ConditionalValidatorFn,
  ConditionDefinition,
  IConditionalValidation,
  IValidationService,
  ValidatorConfig,
} from '@fmnts/declarative-forms';
import { InlineTranslateable } from '@fmnts/i18n';

const enum ValidatorNames {
  NumberRange = 'numberRange',
  NumberRangeConditional = 'numberRangeConditional',
}

type MinValidator = Required<
  Pick<
    NumberRangeArgs & IConditionalValidation,
    'min' | 'condition' | 'messageRefs'
  >
>;

type MaxValidator = Required<
  Pick<
    NumberRangeArgs & IConditionalValidation,
    'max' | 'condition' | 'messageRefs'
  >
>;

interface NumberRangeArgs {
  /**
   * value to be greater than or equal
   */
  min?: number;
  /**
   * value to be less than or equal
   */
  max?: number;
}

interface ValidationErrorsMin extends ValidationErrors {
  min: {
    min: number;
    actual: number;
    message?: InlineTranslateable;
  };
}

interface ValidationErrorsMax extends ValidationErrors {
  max: {
    max: number;
    actual: number;
    message?: InlineTranslateable;
  };
}

export type NumberRangeConditionalValidationConfig =
  ValidatorConfig<NumberRangeArgs>;

export interface ValidationErrorsConditionalMin extends ValidationErrors {
  conditionalMin: {
    min: number;
    actual: number;
    condition: ConditionDefinition;
    message?: InlineTranslateable;
  };
}

export interface ValidationErrorsConditionalMax extends ValidationErrors {
  conditionalMax: {
    max: number;
    actual: number;
    condition: ConditionDefinition;
    message?: InlineTranslateable;
  };
}

/**
 * Type guard for checking a "conditionalMin" validation error
 *
 * @param e Errors object
 *
 * @returns
 * `true` if the passed errors object has "conditionalMin"
 * validation errors
 */
export function hasConditionalMinError(
  e: Record<string, any>,
): e is ValidationErrorsConditionalMin {
  return 'conditionalMin' in e;
}

/**
 * Type guard for checking a "min" validation error
 *
 * @param e Errors object
 *
 * @returns
 * `true` if the passed errors object has "min"
 * validation errors
 */
export function hasMinError(
  e: Record<string, unknown>,
): e is ValidationErrorsMin {
  return 'min' in e;
}

/**
 * Type guard for checking a "max" validation error
 *
 * @param e Errors object
 *
 * @returns
 * `true` if the passed errors object has "max"
 * validation errors
 */
export function hasMaxError(
  e: Record<string, unknown>,
): e is ValidationErrorsMax {
  return 'max' in e;
}

/**
 * Service that lets you create number range validators
 */
@Injectable({
  providedIn: 'root',
})
export class NumberRangeValidationService implements IValidationService {
  private conditionalValidationService = inject(ConditionalValidationService);

  public buildValidator(validator: ValidatorConfig): ValidatorFn[] {
    switch (validator.name) {
      case ValidatorNames.NumberRange:
        return this.buildNumberRangeValidations(
          validator as ValidatorConfig<NumberRangeArgs>,
        );
      case ValidatorNames.NumberRangeConditional:
        return [
          ...this.buildNumberRangeConditionalValidations(
            validator as NumberRangeConditionalValidationConfig,
          ),
        ];
      default:
        return [];
    }
  }

  private buildNumberRangeValidations(
    validator: ValidatorConfig<NumberRangeArgs>,
  ): ValidatorFn[] {
    const minValidator = validator.args.min
      ? transformValidationErrors(
          Validators.min(validator.args.min),
          (errors: ValidationErrorsMin) => ({
            min: { ...errors.min, message: validator.messages?.['min'] },
          }),
        )
      : undefined;

    const maxValidator = validator.args.max
      ? transformValidationErrors(
          Validators.max(validator.args.max),
          (errors: ValidationErrorsMax) => ({
            max: { ...errors.max, message: validator.messages?.['max'] },
          }),
        )
      : undefined;

    return [minValidator, maxValidator].filter(isDefined);
  }

  /**
   * @returns
   * Validator function that returns null if the condition is not
   * fulfilled or if the controls value is greater than or equal to `min`.
   */
  private withConditionalMin(
    condition: ConditionDefinition,
    min: number,
    message?: InlineTranslateable,
  ) {
    return this.conditionalValidationService.withCondition(
      condition,
      // Wrap validation errors
      transformValidationErrors(
        Validators.min(min),
        (errors: ValidationErrorsMin): ValidationErrorsConditionalMin => ({
          conditionalMin: {
            ...errors.min,
            condition,
            message,
          },
        }),
      ),
    );
  }

  /**
   * @returns
   * Validator function that returns null if the condition is not
   * fulfilled or if the controls value is less than or equal to `max`.
   */
  private withConditionalMax(condition: ConditionDefinition, max: number) {
    return this.conditionalValidationService.withCondition(
      condition,
      // Wrap validation errors
      transformValidationErrors(
        Validators.max(max),
        (errors: ValidationErrorsMax): ValidationErrorsConditionalMax => ({
          conditionalMax: {
            ...errors.max,
            condition,
          },
        }),
      ),
    );
  }

  /**
   * @param opts Options from which the validator should be builded
   *
   * @returns
   * A single composed validator function from the given `opts`
   * that uses Angulars `Validators.min` validator
   */
  private composeMinValidator(
    opts: NumberRangeConditionalValidationConfig,
  ): ConditionalValidatorFn | null {
    const { args, conditionalArgs = [], messages = {} } = opts;

    // Filter for args with `min` and sort descending
    const conditionalValidators = conditionalArgs
      .filter((validator): validator is MinValidator =>
        hasProperty('min', isNumber)(validator),
      )
      .sort((a, b) => a.min - b.min)
      .map((arg) =>
        this.withConditionalMin(
          arg.condition,
          arg.min,
          arg.messageRefs
            ? messages[arg.messageRefs['conditionalMin']]
            : undefined,
        ),
      );

    const staticValidator =
      'min' in args && args.min ? [Validators.min(args.min)] : [];

    return this.conditionalValidationService.compose([
      ...conditionalValidators,
      ...staticValidator,
    ]);
  }

  /**
   * @param opts Options from which the validator should be builded
   *
   * @returns
   * A single composed validator function from the given `opts`
   * that uses Angulars `Validators.max` validator
   */
  private composeMaxValidator(
    opts: NumberRangeConditionalValidationConfig,
  ): ConditionalValidatorFn | null {
    const { args, conditionalArgs = [] } = opts;

    //  Filter for args with `max` and sort ascending
    const conditionalValidators = conditionalArgs
      .filter((validator): validator is MaxValidator =>
        hasProperty('max', isNumber)(validator),
      )
      .sort((a, b) => b.max - a.max)
      .map((arg) => this.withConditionalMax(arg.condition, arg.max));

    const staticValidator =
      'max' in args && args.max ? [Validators.max(args.max)] : [];

    return this.conditionalValidationService.compose([
      ...conditionalValidators,
      ...staticValidator,
    ]);
  }

  /**
   * @param opts The options to parse
   *
   * @returns
   * An array of conditional validator functions based on the given
   * `opts`.
   */
  public buildNumberRangeConditionalValidations(
    opts: NumberRangeConditionalValidationConfig,
  ): ConditionalValidatorFn[] {
    const validators: ConditionalValidatorFn[] = [];

    const composedMinValidator = this.composeMinValidator(opts);
    if (composedMinValidator) {
      validators.push(composedMinValidator);
    }

    const composedMaxValidator = this.composeMaxValidator(opts);
    if (composedMaxValidator) {
      validators.push(composedMaxValidator);
    }
    return validators;
  }
}
